'use client'

/**
 * Person Mutation Hooks
 *
 * The person vocabulary over `document/`'s operations. The mechanics moved there in #289; this is
 * the translation, and it moves to `entries/person/write-spec.ts` in step 2.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  useDocumentOps,
  type EntryChange,
  type MetadataPatch,
  useDocumentView,
} from '@/document'
// The person slice's drafts are still Supabase row shapes.
// eslint-disable-next-line import/no-restricted-paths
import type { PersonInsert, PersonUpdate } from '@/legacy/supabase-store'
import { peopleQueryKey, personEntriesQueryKey } from './select-people'

// ============================================================================
// People Mutations
// ============================================================================

export function useCreatePerson() {
  const doc = useDocumentView()
  const ops = useDocumentOps()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: PersonInsert) => {
      if (!doc) {
        throw new Error('Document not ready')
      }

      const id = ops.createEntry({
        type: 'person',
        title: data.name,
        content: data.notes || '',
        metadata: {
          category: data.category,
          birthday: data.birthday,
        },
      })

      return doc.entries[id]
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: peopleQueryKey })
    },
  })
}

export function useUpdatePerson() {
  const doc = useDocumentView()
  const ops = useDocumentOps()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: PersonUpdate }) => {
      if (!doc) {
        throw new Error('Document not ready')
      }

      const metadata: MetadataPatch = {}
      if (data.category !== undefined) metadata.category = data.category
      if (data.birthday !== undefined) metadata.birthday = data.birthday

      const change: EntryChange = { metadata }
      if (data.name !== undefined) change.title = data.name
      if (data.notes !== undefined) change.content = data.notes || ''

      if (!ops.updateEntry(id, change)) {
        throw new Error(`Person ${id} not found`)
      }

      return doc.entries[id]
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: peopleQueryKey })
      queryClient.invalidateQueries({
        queryKey: personEntriesQueryKey(variables.id),
      })
    },
  })
}

export function useDeletePerson() {
  const doc = useDocumentView()
  const ops = useDocumentOps()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      if (!doc) {
        throw new Error('Document not ready')
      }

      if (!ops.archiveEntry(id)) {
        throw new Error(`Person ${id} not found`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: peopleQueryKey })
    },
  })
}
